const WS_URL =
  process.env.REACT_APP_PARTNERS_WS_URL ||
  "wss://websocket.tnibro.com/ws?username=stockyaari_backend&password=stockyaari@8092";
const RECONNECT_DELAY = 3000;

class PartnersWSManager {
  constructor() {
    this.ws = null;
    this.subscribers = new Map(); // symbol (uppercase) -> Set of callbacks
    this.statusListeners = new Set(); // (isConnected: bool) => void
    this.reconnectTimer = null;
  }

  subscribe(symbol, callback) {
    const key = symbol.toUpperCase();
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, new Set());
    }
    this.subscribers.get(key).add(callback);  

    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      this._connect();
    } else if (this.ws.readyState === WebSocket.OPEN) {
      this._sendSubscribe();
    }

    return () => this._unsubscribe(key, callback);
  }

  // Bulk-subscribe a list of symbols at once (call after fetching trades)
  bulkSubscribe(symbols) {
    symbols.forEach((sym) => {
      const key = sym.toUpperCase();
      if (!this.subscribers.has(key)) {
        this.subscribers.set(key, new Set());
      }
    });

    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      this._connect();
    } else if (this.ws.readyState === WebSocket.OPEN) {
      this._sendSubscribe();
    }
  }

  subscribeStatus(callback) {
    this.statusListeners.add(callback);
    // immediately fire current state
    callback(this.ws?.readyState === WebSocket.OPEN);
    return () => this.statusListeners.delete(callback);
  }

  _notifyStatus(connected) {
    this.statusListeners.forEach((cb) => cb(connected));
  }

  _unsubscribe(key, callback) {
    const set = this.subscribers.get(key);
    if (!set) return;
    set.delete(callback);
    if (set.size === 0) this.subscribers.delete(key);
    if (this.subscribers.size === 0) this._disconnect();
  }

  _sendSubscribe() {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const symbols = [...this.subscribers.keys()];
    if (symbols.length === 0) return;
    this.ws.send(JSON.stringify({ action: "subscribe", symbols }));
  }

  _connect() {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    )
      return;

    this.ws = new WebSocket(WS_URL);

    this.ws.onopen = () => {
      clearTimeout(this.reconnectTimer);
      this._notifyStatus(true);
      this._sendSubscribe();
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const key = data.symbol?.toUpperCase();
        if (key && this.subscribers.has(key)) {
          this.subscribers.get(key).forEach((cb) => cb(data));
        }
      } catch (err) {
        console.error("[PartnersWS] parse error:", err);
      }
    };

    this.ws.onerror = (err) => {
      console.error("[PartnersWS] error:", err);
    };

    this.ws.onclose = () => {
      this.ws = null;
      this._notifyStatus(false);
      if (this.subscribers.size > 0) {
        this.reconnectTimer = setTimeout(() => this._connect(), RECONNECT_DELAY);
      }
    };
  }

  _disconnect() {
    clearTimeout(this.reconnectTimer);
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this._notifyStatus(false);
  }
}

// Singleton — one connection shared across all components
export const partnersWSManager = new PartnersWSManager();
